import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
import { ViewModelServiceContext, withViewModelService } from '@xengage/gw-portals-viewmodel-react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/prefer-default-export
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { PolicyChange, messages as commonMessages } from 'gw-capability-policychange-common-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { FormModalPopover, platformMessages as mlCommonMessages } from 'ml-components-platform-react';
import { MLModalService , MLTagManagerService } from 'ml-portals-services-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';

import { MLLandingPageProvider } from 'gw-capability-gateway-quoteandbind-common-react';
import messages from './PAPolicyChange.messages';
import gpaWizardConfig from './config/pa-policychange-wizard-config.json5';
import ampWizardConfig from './config/pa-amp-wizard-config.json5';
import styles from './PAPolicyChange.module.scss';

function PAPolicyChangeWizard(props) {
    const modalApi = useModal();
    const wizardConfig = appConfig.portalCode === 'GPA' ? gpaWizardConfig : ampWizardConfig;
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const [additionalProps, setAdditionalProps] = useState({
    isPolicyChangeAndTrasferToPayrollFlow: false
  });
    const [isLoading, setIsLoading] = useState(true);
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history } = props;
    const { authHeader } = useAuthentication();
    const { EndorsementService } = useDependencies('EndorsementService');

    const translator = useTranslator();

    const {
        showErrorModal,
        showProgressDialog
    } = MLModalService();

    useEffect(
        () => {
            if (!location.state) {
                history.push('/');
                return;
            }
            const {
                state: { policyNumber, chosenStepPathML }
            } = location;
            if (!policyNumber) {
                return;
            }
            const errorModalBox = (errorMessage) => {
                modalApi.showConfirm(errorMessage).then((results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }
                    setIsLoading(false);
                    return history.push(`/contactAgent/${policyNumber}`);
                }, _.noop);
            };

            // ML - Removed get available policy call here - as per V9
            const promise = EndorsementService.loadEndorsement(policyNumber, authHeader);
            showProgressDialog(messages.loadingPolicyChange, promise);
            promise
                .then((responseData) => {
                    const submission = viewModelService.create(
                        new PolicyChange(responseData),
                        'pc',
                        'edge.capabilities.policychange.dto.PolicyChangeDataDTO'
                    );
                    submission.value.chosenStepPathML = chosenStepPathML;
                    setInitialSubmission(submission);
                    setIsLoading(false);

                    if(submission.value.chosenStepPathML==='/transfer-to-payroll') {
                        setAdditionalProps({...additionalProps,isPolicyChangeAndTrasferToPayrollFlow:true});
                    }
                    
                    if (responseData.jobID) {
                        ClausesUtil.getDependentClausesFromServer(
                            ClauseService,
                            _.get(responseData, 'jobID'),
                            authHeader
                        );
                    }
                    
                })
                .catch((error) => {
                    console.error(error);
                    errorModalBox({
                        title: commonMessages.unableToLoadDraftPolicy,
                        message: commonMessages.somethingWentWrong,
                        status: 'warning',
                        icon: 'mi-error-outline'
                    });
                })
                .catch(() => {
                    errorModalBox({
                        title: commonMessages.unableToStartPolicy,
                        message: commonMessages.anErrorOccurred,
                        status: 'warning',
                        icon: 'mi-error-outline'
                    });
                });
            return () => {
                MLTagManagerService.setModel();
            }
        },
        // Disabled so we don't rerun this function on every rerender
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const handleOnCancel = useCallback((cancelParams) => {
        const { wizardData, wizardSnapshot, param: nextPath = '', currentStepIndex, steps: wizardSteps } = cancelParams;
        const { submissionVM } = wizardData;
        const status = _.get(submissionVM.value, 'status');
        const jobID = _.get(submissionVM.value, 'jobID');
        const policyNumber = _.get(submissionVM.value, 'policyNumber');
        const withdrawPolicyChange = _.get(submissionVM, 'value.withdrawPolicyChange', false);
        const confirmationPageIndex = wizardSteps.length - 1;
        if ((status === 'Bound' && !_.isNil(jobID)) || (nextPath.startsWith('/change') || nextPath.startsWith('/contactAgent') || nextPath.startsWith('/new-quote') || nextPath.startsWith('/contactUs'))
            || hasErrorOccurred || withdrawPolicyChange || currentStepIndex === confirmationPageIndex) {
            return true;
        }

        return modalApi.showModal(
            <FormModalPopover
                title={translator(mlCommonMessages.areYouSureYouWantToCancel)}
                simpleMessage={translator(mlCommonMessages.areYouSureYouWantToCancelThisPolicyChange)}
                actionBtnLabel={platformMessages.yes}
                cancelBtnLabel={mlCommonMessages.mlCancel}
                closeSimpleMessage={false}
            />
        ).then(() => {
            setIsLoading(true);
            const isValid = submissionVM.aspects.subtreeValid && submissionVM.aspects.valid;
            const payload = isValid ? submissionVM.value : _.get(wizardSnapshot, 'submissionVM.value');
            payload.beforeSave();
            const quoteStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code')
                || _.get(submissionVM.value, 'status');
            let redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/change/${jobID}/summary`;
            if (!jobID) {
                redirectLobPath = `/policies/${policyNumber}/summary`;
            }
            let exitPromise;
            if (appConfig.persona === 'policyholder') {
                redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/account-policy-details/${policyNumber}`;
            }
            if (appConfig.persona === 'policyholder') {
                if (quoteStatus !== 'Bound') {
                    MLTagManagerService.sendLinkClickEvent('Discard All Changes');
                    exitPromise = EndorsementService.withdrawEndorsement([jobID], authHeader);
                } else {
                    return history.push(redirectLobPath);
                }
            } else if (quoteStatus === 'Quoted') {
                exitPromise = EndorsementService.saveEndorsement([payload], authHeader);
            } else if (quoteStatus === 'Draft') {
                exitPromise = EndorsementService.withdrawEndorsement([jobID], authHeader);
            } else {
                return history.push(redirectLobPath);
            }
            exitPromise.then(() => {
                history.push(redirectLobPath);
            }).catch(() => {
                history.push(redirectLobPath);
                showErrorModal(
                    commonMessages.unableToDraftPolicy,
                    messages.anErrorOccurred,
                    platformMessages.ok
                );
            }).finally(() => {
                setIsLoading(false);
            });
            return true;
        }, _.noop);
    }, [hasErrorOccurred, translator, EndorsementService, authHeader, history, showErrorModal]);

    const handleError = useCallback(
        (error) => {
            const state = {
                pathname: '/endorsementContactUs',
                state: error
            };

            setHasErrorOccurred(true);
            return <Redirect push to={state} />
        }, 
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    if (isLoading) {
        return <></>;
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <div className={styles.paPolicyChangeWizardContainer}>
            <ErrorBoundary onError={handleError}>
                <MLLandingPageProvider additionalPolicyChangeProps ={additionalProps}>
                <Wizard
                    initialSteps={steps}
                    wizardTitle={title}
                    initialData={{
                        submissionVM: initialSubmission,
                        selectedPages: {
                            address: false,
                            vehicles: false,
                            drivers: false,
                            coverages: false
                        },
                        alertMessages: []
                    }}
                    onCancel={handleOnCancel}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        status: 'warning',
                        icon: 'mi-error-outline',
                        confirmButtonText: platformMessages.yes,
                        cancelButtonText: platformMessages.no
                    }}
                    extractVMObject={(data) => _.get(data, 'submissionVM')}
                />
                </MLLandingPageProvider>
            </ErrorBoundary>
        </div>
    );
}

PAPolicyChangeWizard.propTypes = {
    viewModelService: PropTypes.shape({
        create: PropTypes.func
    }).isRequired,
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            requestType: PropTypes.string,
            policyDetails: PropTypes.shape({
                effective: PropTypes.instanceOf(Date)
            })
        })
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    }).isRequired
};

export default withRouter(withViewModelService(PAPolicyChangeWizard));
